import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Pencil, SlidersHorizontal, History, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { LoadingState, ErrorState, EmptyState, Field, FormError, SubmitButton, Badge } from "@/components/common";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg } from "@/lib/api";
import { fmtCents, fmtDateTime } from "@/lib/format";
import { resetPartCache } from "@/modules/inventory/PartPicker";
import { t } from "@/i18n";

const EMPTY = { sku: "", name: "", brand: "", supplier_id: "", cost_price: "", sell_price: "", gst: true, unit: "each", reorder_at: "0", location: "", active: true };

export function useSave(fn, onDone, ok) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    setError("");
    try {
      await fn();
      if (ok) toast.success(ok);
      resetPartCache();
      onDone();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, submit };
}

function PartDialog({ part, suppliers, onClose, onDone }) {
  const [f, setF] = useState(part ? { ...EMPTY, ...part, supplier_id: part.supplier_id || "", cost_price: (part.cost_cents / 100).toFixed(2), sell_price: (part.sell_cents / 100).toFixed(2), reorder_at: String(part.reorder_at) } : EMPTY);
  const body = { ...f, supplier_id: f.supplier_id || null, cost_price: f.cost_price || "0", sell_price: f.sell_price || "0", reorder_at: f.reorder_at || "0" };
  const { busy, error, submit } = useSave(() => (part ? api.put(`/inventory/parts/${part.id}`, body) : api.post("/inventory/parts", body)), onDone, t("b8.inv.saved"));
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="part-dialog">
        <DialogHeader><DialogTitle>{part ? t("b8.inv.editPart") : t("b8.inv.newPart")}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2"><FormError message={error} /></div>
          <Field label="SKU" htmlFor="p-sku"><Input id="p-sku" required value={f.sku} onChange={set("sku")} data-testid="part-sku-input" /></Field>
          <Field label={t("b8.inv.name")} htmlFor="p-name"><Input id="p-name" required value={f.name} onChange={set("name")} data-testid="part-name-input" /></Field>
          <Field label={t("b8.inv.brand")} htmlFor="p-brand"><Input id="p-brand" value={f.brand} onChange={set("brand")} data-testid="part-brand-input" /></Field>
          <Field label={t("b8.inv.supplier")}>
            <Select value={f.supplier_id || "none"} onValueChange={(v) => setF({ ...f, supplier_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="part-supplier-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="none">—</SelectItem>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label={t("b8.inv.cost")} htmlFor="p-cost"><Input id="p-cost" type="number" min={0} step="0.01" value={f.cost_price} onChange={set("cost_price")} data-testid="part-cost-input" /></Field>
          <Field label={t("b8.inv.sell")} htmlFor="p-sell"><Input id="p-sell" type="number" min={0} step="0.01" value={f.sell_price} onChange={set("sell_price")} data-testid="part-sell-input" /></Field>
          <Field label={t("b8.inv.unit")} htmlFor="p-unit"><Input id="p-unit" value={f.unit} onChange={set("unit")} data-testid="part-unit-input" /></Field>
          <Field label={t("b8.inv.reorder")} htmlFor="p-re"><Input id="p-re" type="number" min={0} value={f.reorder_at} onChange={set("reorder_at")} data-testid="part-reorder-input" /></Field>
          <Field label={t("b8.inv.location")} htmlFor="p-loc"><Input id="p-loc" value={f.location} onChange={set("location")} data-testid="part-location-input" /></Field>
          <div className="flex flex-col justify-end gap-2 text-sm">
            <label className="flex items-center gap-2"><Switch checked={f.gst} onCheckedChange={(v) => setF({ ...f, gst: v })} data-testid="part-gst-switch" />{t("b8.inv.gst")}</label>
            <label className="flex items-center gap-2"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="part-active-switch" />{t("b8.inv.active")}</label>
          </div>
          <div className="gf-modal-footer sm:col-span-2"><SubmitButton busy={busy} className="w-full" data-testid="part-save-button">{t("common.save")}</SubmitButton></div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AdjustDialog({ part, onClose, onDone }) {
  const [f, setF] = useState({ qty: "", reason: "" });
  const { busy, error, submit } = useSave(() => api.post(`/inventory/parts/${part.id}/adjust`, f), onDone, t("b8.inv.adjusted"));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="adjust-dialog">
        <DialogHeader><DialogTitle>{t("b8.inv.adjustTitle", { name: part.name })}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <FormError message={error} />
          <Field label={t("b8.inv.adjustQty")} htmlFor="a-qty" hint={t("b8.inv.adjustHint")}><Input id="a-qty" type="number" step="any" required value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} data-testid="adjust-qty-input" /></Field>
          <Field label={t("b8.inv.reason")} htmlFor="a-reason"><Input id="a-reason" required minLength={3} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="adjust-reason-input" /></Field>
          <SubmitButton busy={busy} className="w-full" data-testid="adjust-save-button">{t("common.save")}</SubmitButton>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function MovesDialog({ part, onClose }) {
  const { data } = useFetch(`/inventory/parts/${part.id}/moves`);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl" data-testid="moves-dialog">
        <DialogHeader><DialogTitle>{t("b8.inv.history", { name: part.name })}</DialogTitle></DialogHeader>
        {!data ? <LoadingState /> : data.length === 0 ? <p className="text-sm text-slate-500">{t("b8.inv.noMoves")}</p> : (
          <Table><TableHeader><TableRow><TableHead>{t("b8.inv.when")}</TableHead><TableHead>{t("b8.inv.kind")}</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">{t("b8.inv.after")}</TableHead></TableRow></TableHeader>
            <TableBody>{data.map((m) => (
              <TableRow key={m.id} data-testid={`move-${m.kind}`}><TableCell className="text-xs">{fmtDateTime(m.at)}<span className="block text-slate-500">{m.by}</span></TableCell>
                <TableCell className="text-sm">{t(`b8.inv.moves.${m.kind}`)} {m.ref_number && <span className="text-slate-500">· {m.ref_number}</span>}{m.reason && <span className="block text-xs text-slate-500">{m.reason}</span>}</TableCell>
                <TableCell className={`text-right font-semibold ${m.qty < 0 ? "text-red-600" : "text-emerald-700"}`}>{m.qty > 0 ? "+" : ""}{m.qty}</TableCell><TableCell className="text-right">{m.qty_after}</TableCell></TableRow>
            ))}</TableBody></Table>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function PartsTab({ canManage }) {
  const [params] = useSearchParams();
  const [q, setQ] = useState("");
  const [low, setLow] = useState(params.get("low") === "1");
  const { data, error, loading, reload } = useFetch(`/inventory/parts?q=${encodeURIComponent(q)}&low=${low}&include_archived=${canManage}`);
  const suppliers = useFetch("/inventory/suppliers");
  const [dlg, setDlg] = useState(() => (canManage && params.get("new") === "1" ? { kind: "edit" } : null));
  const done = () => { setDlg(null); reload(); };
  return (
    <div className="space-y-4" data-testid="parts-tab">
      <div className="flex flex-wrap items-center gap-3">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("b8.inv.searchParts")} className="max-w-xs" data-testid="parts-search-input" />
        <label className="flex items-center gap-2 text-sm"><Switch checked={low} onCheckedChange={setLow} data-testid="parts-low-switch" />{t("b8.inv.lowOnly")}</label>
        {canManage && <Button className="ml-auto" onClick={() => setDlg({ kind: "edit" })} data-testid="new-part-button"><Plus className="mr-1.5 h-4 w-4" />{t("b8.inv.newPart")}</Button>}
      </div>
      {loading && !data && <LoadingState />}
      {error && <ErrorState message={error} onRetry={reload} />}
      {data?.length === 0 && <EmptyState title={t("b8.inv.noParts")} />}
      {data?.length > 0 && (
        <div className="gf-card overflow-x-auto">
          <Table><TableHeader><TableRow><TableHead>SKU</TableHead><TableHead>{t("b8.inv.name")}</TableHead><TableHead>{t("b8.inv.supplier")}</TableHead><TableHead className="text-right">{t("b8.inv.onHand")}</TableHead><TableHead className="text-right">{t("b8.inv.sell")}</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>{data.map((p) => (
              <TableRow key={p.id} className={p.active ? "" : "opacity-50"} data-testid={`part-row-${p.sku}`}>
                <TableCell className="font-mono text-xs">{p.sku}</TableCell>
                <TableCell>{p.name}<span className="block text-xs text-slate-500">{[p.brand, p.location].filter(Boolean).join(" · ")}</span></TableCell>
                <TableCell className="text-sm">{p.supplier_name || "—"}</TableCell>
                <TableCell className="text-right font-semibold" data-testid={`part-qty-${p.sku}`}>{p.qty_on_hand} <span className="text-xs font-normal text-slate-500">{p.unit}</span>
                  {p.negative ? <Badge tone="red" testId={`part-negative-${p.sku}`}><AlertTriangle className="mr-1 inline h-3 w-3" />{t("b8.inv.negative")}</Badge> : p.low && <Badge tone="amber" testId={`part-low-${p.sku}`}>{t("b8.inv.low")}</Badge>}</TableCell>
                <TableCell className="text-right">{fmtCents(p.sell_cents)}</TableCell>
                <TableCell className="whitespace-nowrap text-right">
                  <Button variant="ghost" size="icon" onClick={() => setDlg({ kind: "moves", part: p })} aria-label="history" data-testid={`part-history-${p.sku}`}><History className="h-4 w-4" /></Button>
                  {canManage && <Button variant="ghost" size="icon" onClick={() => setDlg({ kind: "adjust", part: p })} aria-label="adjust" data-testid={`part-adjust-${p.sku}`}><SlidersHorizontal className="h-4 w-4" /></Button>}
                  {canManage && <Button variant="ghost" size="icon" onClick={() => setDlg({ kind: "edit", part: p })} aria-label="edit" data-testid={`part-edit-${p.sku}`}><Pencil className="h-4 w-4" /></Button>}
                </TableCell>
              </TableRow>
            ))}</TableBody></Table>
        </div>
      )}
      {dlg?.kind === "edit" && <PartDialog part={dlg.part} suppliers={suppliers.data || []} onClose={() => setDlg(null)} onDone={done} />}
      {dlg?.kind === "adjust" && <AdjustDialog part={dlg.part} onClose={() => setDlg(null)} onDone={done} />}
      {dlg?.kind === "moves" && <MovesDialog part={dlg.part} onClose={() => setDlg(null)} />}
    </div>
  );
}
